import React from 'react';
import { Link } from 'react-router-dom';
import { useT } from '../i18n';
import { SHARE_KINDS } from '../constants/share';
import { shareHref } from '../firebase/chatService';

// ── A SHARED PIECE OF WORK, INSIDE A MESSAGE ─────────────────────────────────
// The card a `share` field renders as: a thumbnail, what kind of thing it is,
// its title, and a link to the real thing.
//
// A REAL LINK, not a button with an onClick. Middle-click, ⌘-click and "open in
// new tab" all work, which for something whose whole purpose is "go and look at
// this" is not a detail — and the chat overlay closes itself on navigation
// (see ChatUIContext), so an ordinary click behaves too.
//
// EVERY DESTINATION ALREADY EXISTS — see shareHref. A project opens its public
// /share page; a gallery item opens the deep link its owner's profile already
// understands. Nothing here needs a route of its own.

// The placeholder when a card has no picture, which is every project share:
// projects carry a scene, not a rendered cover, so there is nothing to shrink.
// Same reasoning as the workspace's own card thumbnails, which are inline SVG
// for exactly this reason.
const IcoPlan = (
  <svg viewBox="0 0 24 24" width="26" height="26" fill="none" stroke="currentColor"
    strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M3 10.5 12 4l9 6.5" />
    <path d="M5 9.8V20h14V9.8" />
    <path d="M10 20v-5.5h4V20" />
  </svg>
);

export default function ChatShareCard({ share, suppressed = false, chatId = '' }) {
  const { t } = useT();
  // The conversation rides along so the destination can bring the reader back
  // to it when they close what they opened — see shareHref.
  const href = shareHref(share, chatId);

  // A card whose kind or ids did not survive — an older message, or one written
  // by a client this one does not know about. Shown as plain unavailable text
  // rather than a link that goes nowhere, which is how a lost reply quote
  // behaves too.
  if (!href) return <span className="ct-share ct-share--gone">{t('share_msg_gone')}</span>;

  const label = share.kind === SHARE_KINDS.PHOTO ? t('share_kind_photo')
    : share.kind === SHARE_KINDS.DESIGN ? t('share_kind_design')
      : t('share_kind_project');

  return (
    <Link
      className="ct-share"
      to={href}
      // WHILE THE MESSAGE MENU IS OPEN, THE CARD IS NOT A LINK. A press-and-
      // hold on a card opens that menu and still ends in a `click` — without
      // this, holding a shared design to reply to it would navigate away
      // instead, and the menu would be gone before it was read.
      onClick={(e) => { if (suppressed) e.preventDefault(); e.stopPropagation(); }}
    >
      <span className="ct-share-thumb">
        {share.thumb
          ? <img src={share.thumb} alt="" draggable={false} />
          : IcoPlan}
      </span>
      <span className="ct-share-body">
        <span className="ct-share-kind">{label}</span>
        <span className="ct-share-title">{share.title || t('share_msg_untitled')}</span>
        <span className="ct-share-open">{t('share_msg_open')}</span>
      </span>
    </Link>
  );
}
